"use client";

import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Video, Calendar, MapPin, CreditCard, CircleCheck as CheckCircle, Users, HeartHandshake } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
const sessions = [{
  id: 1,
  number: "Cercle 1",
  title: "Ouverture du cercle",
  description: "Présentation, moment d’échange et création du lien entre mamans.",
  animator: "Animé par une accompagnante postnatale."
}, {
  id: 2,
  number: "Cercle 2",
  title: "Corps postpartum & rééducation du périnée",
  description: "",
  animator: "Animé par une physiothérapeute périnatale."
}, {
  id: 3,
  number: "Cercle 3",
  title: "Portage physiologique",
  description: "",
  animator: "Animé par une monitrice de portage."
}, {
  id: 4,
  number: "Cercle 4",
  title: "Massage bébé",
  description: "",
  animator: "Animé par une professionnelle formée au massage bébé."
}, {
  id: 5,
  number: "Cercle 5",
  title: "Santé émotionnelle en postpartum",
  description: "",
  animator: "\xA0Animé par une coach en santé mentale périnatale."
}, {
  id: 6,
  number: "Cercle 6",
  title: "Développement du nourrisson & activités Montessori",
  description: "",
  animator: "Animé par une guide Montessori 0–3 ans."
}];
const bonuses = [{
  id: 1,
  title: "Sommeil du nourrisson",
  animator: "Animé par une consultante en sommeil du nourrisson."
}, {
  id: 2,
  title: "Huiles essentielles en postpartum",
  animator: "Animé par une conseillère en huiles essentielles."
}];
const practicalInfos = [{
  id: "quand",
  icon: Calendar,
  title: "QUAND",
  content: "Les rencontres auront lieu un jour de semaine, de 9h30 à 11h00. Les dates exactes seront communiquées dès que le cercle sera confirmé."
}, {
  id: "ou",
  icon: MapPin,
  title: "OÙ",
  content: "À Nyon. La localisation exacte te sera envoyée quelques jours avant le début du cercle."
}, {
  id: "tarif",
  icon: CreditCard,
  title: "TARIF",
  content: "Tarif Early Bird : 480 CHF jusqu’au 23.08.2026.\nTarif standard : 500 CHF dès le 24.08.2026.\nPaiement en 2 fois possible."
}, {
  id: "reservation",
  icon: CheckCircle,
  title: "RÉSERVATION",
  content: "Ta place est réservée avec un acompte de 50 CHF, déduit du montant final.\nL’acompte n’est pas remboursable en cas de désistement de ta part, sauf si le cercle ne peut pas avoir lieu, si la cohorte est annulée de notre côté ou si les dates exactes communiquées ne te conviennent finalement pas."
}, {
  id: "parrainage",
  icon: Users,
  title: "PARRAINAGE",
  content: "Tu recommandes le cercle à une amie ?\nSi elle s’inscrit, vous recevez chacune 30 CHF de réduction.\nOffre valable une fois par participante et appliquée uniquement lorsque les deux inscriptions sont confirmées."
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};
export default function CircleProgramOverview() {
  return <section id="circleprogramoverview" className="bg-background py-16 overflow-hidden !h-[2800.55px] md:py-20">
      <div className="container mx-auto px-6 max-w-6xl">
        
        {}
        <div className="mb-20">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif text-foreground mb-4">
              Le programme des 6 semaines
            </h2>
            <Separator className="w-24 mx-auto bg-primary/30" />
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: "-50px"
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {sessions.map((session, i) => <motion.div key={session.id} variants={itemVariants} data-index={i}>
                <Card className="h-full border-border/50 shadow-sm hover:shadow-md transition-shadow bg-card rounded-lg">
                  <CardHeader className="pb-4">
                    <Badge variant="outline" className="w-fit mb-2 text-primary border-primary/20 bg-primary/5 font-default">
                      {session.number}
                    </Badge>
                    <CardTitle className="font-serif text-xl text-foreground leading-snug">
                      {session.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {session.description && <p className="text-muted-foreground font-default text-sm">
                        {session.description}
                      </p>}
                    <div className="flex items-start gap-2 text-sm font-default text-foreground/80 pt-2 border-t border-border/40">
                      <HeartHandshake className="w-4 h-4 mt-0.5 text-primary/60 shrink-0" />
                      <span>{session.animator}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-secondary/30 rounded-3xl p-8 md:p-12 border border-secondary mb-24">
            <div className="flex flex-col md:flex-row items-center gap-4 mb-8 justify-center text-center md:text-left">
              <div className="bg-primary/10 p-3 rounded-lg text-primary">
                <Video className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-serif text-foreground">Bonus en visio</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {bonuses.map((bonus, i) => <div key={bonus.id} data-index={i} className="bg-background rounded-lg p-6 shadow-sm border border-border/30 flex flex-col justify-center">
                  <h4 className="font-serif text-lg text-foreground mb-3">{bonus.title}</h4>
                  <div className="flex items-start gap-2 text-sm font-default text-muted-foreground">
                    <HeartHandshake className="w-4 h-4 mt-0.5 text-primary/60 shrink-0" />
                    <span>{bonus.animator}</span>
                  </div>
                </div>)}
            </div>
          </motion.div>

          {}
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: "-100px"
        }} variants={containerVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
            <motion.div variants={itemVariants} className="order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <AspectRatio ratio={3 / 4}>
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/20250204_MMP_62-scaled.png" data-wvc-srcset="/img/community_circle_adapted.png-wvc-srcset" data-wvc-sizes="/img/community_circle_adapted.png-wvc-sizes" alt="Groupe de mamans souriantes avec leurs bébés" className="object-cover !w-[437.2px] !h-[446.267px]" loading="lazy" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/20250204_MMP_62-scaled.png" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/20250204_MMP_62-scaled.png" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/20250204_MMP_62-scaled.png" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/20250204_MMP_62-scaled.png" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/20250204_MMP_62-scaled.png" src="https://slow-mama.com/wp-content/uploads/2026/07/20250204_MMP_62-scaled.webp" />
                </AspectRatio>
                <div className="absolute bottom-6 right-6 bg-card/90 backdrop-blur-sm p-4 rounded-2xl shadow-lg flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-lg text-primary">
                    <MessageCircle className="w-6 h-6" />
                  </div>
                  <span className="font-default font-medium text-sm text-foreground">Groupe privé inclus</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="order-1 lg:order-2 space-y-6">
              <Badge variant="secondary" className="bg-secondary/50 text-primary hover:bg-secondary/50 border-none px-4 py-1 text-sm font-default">
                La Communauté
              </Badge>
              <h2 className="text-3xl md:text-4xl font-serif text-foreground leading-tight">
                Le Cercle, c’est aussi une communauté
              </h2>
              <p className="text-lg text-muted-foreground font-default leading-relaxed max-w-md">
                Pendant les 6 semaines, un groupe WhatsApp privé te permettra de rester connectée entre les séances, de poser tes questions, de partager ton quotidien et de créer de vrais liens avec d’autres mamans de la région.
              </p>
            </motion.div>
          </motion.div>
        </div>

        {}
        <div className="mb-20">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif text-foreground mb-4">
              Infos pratiques & tarif
            </h2>
            <Separator className="w-24 mx-auto bg-primary/30" />
          </motion.div>

          <motion.div variants={containerVariants} initial="show" whileInView="show" viewport={{
          once: true,
          margin: "-50px"
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {practicalInfos.map((info, i) => {
            const Icon = info.icon;
            const isLastRow = i >= 3;
            return <motion.div key={info.id} variants={itemVariants} data-index={i} className={`flex ${isLastRow && practicalInfos.length === 5 && i === 3 ? 'lg:col-start-1 lg:col-span-1 lg:translate-x-1/2' : ''} ${isLastRow && practicalInfos.length === 5 && i === 4 ? 'lg:col-start-2 lg:col-span-1 lg:translate-x-1/2' : ''}`}>
                  <Card className="w-full border-border/50 shadow-sm bg-card rounded-lg">
                    <CardHeader className="pb-3 flex flex-row items-center gap-4 space-y-0">
                      <div className="bg-secondary p-2.5 rounded-xl text-primary shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <CardTitle className="font-serif text-lg tracking-wide text-foreground">
                        {info.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground font-default text-sm whitespace-pre-line leading-relaxed max-w-md">
                        {info.content}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-center flex flex-col items-center justify-center">
          <Link to="/ContactPage">
            <Button size="lg" className="text-base px-8 py-6 rounded-lg font-default shadow-md hover:shadow-lg transition-all">
              Je réserve ma place
            </Button>
          </Link>
          <p className="mt-4 text-sm text-muted-foreground font-default flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-primary opacity-40"></span>
              <span className="relative inline-flex rounded-lg h-2.5 w-2.5 bg-primary"></span>
            </span>
            Places limitées à 8 mamans.
          </p>
        </motion.div>

      </div>
    </section>;
}